import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  getGetAgentChatThreadQueryKey,
  useCreateAgentChatThread,
  useCreateAgentChatTurn,
  useGetAgentChatThread,
} from '@/entities/project/api/generated/api';
import type { ProjectAgentChatTurnSchemaStatus } from '@/entities/project/api/generated/model';
import { isTokenLimitError } from '@/entities/system/lib/error-guards';
import type { ChatMessage } from './chat-types';

const POLLING_INTERVAL_MS = 5000;
const WAITING_STATUSES = new Set<ProjectAgentChatTurnSchemaStatus>(['idle', 'generating']);
const THREAD_TITLE_MAX_LENGTH = 50;
export const useAgentChat = ({
  projectId,
  initialThreadId,
  onThreadCreated,
}: {
  projectId: string;
  initialThreadId?: string;
  onThreadCreated?: (threadId: string) => void;
}) => {
  const [threadId, setThreadId] = useState<string | undefined>(initialThreadId);
  const [isTokenLimitExceeded, setIsTokenLimitExceeded] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const queryClient = useQueryClient();

  // 履歴選択等で外部から initialThreadId が変わった場合に同期
  useEffect(() => {
    setThreadId(initialThreadId);
  }, [initialThreadId]);

  const createThreadMutation = useCreateAgentChatThread();
  const createTurnMutation = useCreateAgentChatTurn();

  const {
    data: thread,
    isError,
    isPending: isThreadPending,
  } = useGetAgentChatThread(projectId, threadId ?? '', {
    query: {
      enabled: !!threadId,
      refetchInterval: (query) => {
        const turns = query.state.data?.turns;
        if (!turns || turns.length === 0) return false;
        const lastTurn = turns[turns.length - 1];
        return lastTurn && WAITING_STATUSES.has(lastTurn.status) ? POLLING_INTERVAL_MS : false;
      },
      refetchOnWindowFocus: false,
      retry: false,
    },
  });

  // 無効なスレッド（削除済み・他プロジェクト等）を検知したら新規チャットにフォールバック
  useEffect(() => {
    if (isError && threadId) {
      setThreadId(undefined);
    }
  }, [isError, threadId]);

  const projectPrompt = thread?.turns?.at(0)?.project_prompt || undefined;

  const messages: ChatMessage[] = (thread?.turns ?? []).flatMap((turn) => {
    const userMsg: ChatMessage = {
      id: `user-${turn.id}`,
      role: 'user',
      content: turn.prompt,
    };

    if ((turn.status === 'completed' || turn.status === 'failed') && turn.completion) {
      const assistantMsg: ChatMessage = {
        id: `assistant-${turn.id}`,
        role: 'assistant',
        content: turn.completion,
        projectPrompt,
      };
      return [userMsg, assistantMsg];
    }

    return [userMsg];
  });

  const lastTurn = thread?.turns?.at(-1);
  const isLoading = isSending || (!!lastTurn && WAITING_STATUSES.has(lastTurn.status));
  // 履歴選択直後など、threadId が指定されていてスレッド本体取得中の状態。
  // この間にウェルカム画面がフラッシュしないよう、呼び出し側で Skeleton を出す判定に使う。
  const isThreadFetching = !!threadId && isThreadPending;

  const sendMessage = async (content: string) => {
    if (isTokenLimitExceeded) return;
    setIsSending(true);
    try {
      let currentThreadId = threadId;

      if (!currentThreadId) {
        const result = await createThreadMutation.mutateAsync({
          projectId,
          data: { title: content.slice(0, THREAD_TITLE_MAX_LENGTH) },
        });
        currentThreadId = result.id;
        setThreadId(currentThreadId);
        onThreadCreated?.(currentThreadId);
      }

      await createTurnMutation.mutateAsync({
        projectId,
        threadId: currentThreadId,
        data: { prompt: content },
      });

      await queryClient.invalidateQueries({
        queryKey: getGetAgentChatThreadQueryKey(projectId, currentThreadId),
      });
    } catch (error) {
      if (isTokenLimitError(error)) {
        setIsTokenLimitExceeded(true);
      }
    } finally {
      setIsSending(false);
    }
  };

  return {
    messages,
    isLoading,
    isThreadFetching,
    sendMessage,
    isTokenLimitExceeded,
    isError,
    threadId,
  };
};
